// RecordsPage.js — /records: written introductions plus live tables built from
// the API: the scorigami timeline (first time each home/away result
// happened), the rarest results, biggest wins, highest-scoring matches, and
// the most common results.

import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Skeleton, Typography } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import { CtaRow, H2, InternalLink, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { cachedScores, fetchScores, mapLimit } from "./api";
import { HOME_AWAY, displayName, formatDate, orientOccurrence, summaryCells, toWinnerFirst } from "./components/heatmap/utils";

const parseKey = (key) => key.split("-").map(Number);

// Only the winner-first scorelines that can hold a big win or a high-scoring
// game need their full match lists.
const recordKeys = (summary) =>
  summary
    .map((r) => r.score)
    .filter((k) => {
      const [hi, lo] = parseKey(k);
      return hi - lo >= 7 || hi + lo >= 9;
    });

// Everything the page needs from the pre-render cache, or null if anything is missing.
function seededRecords() {
  const summary = cachedScores("summary=1")?.items;
  if (!summary) return null;
  const lists = recordKeys(summary).map((k) => cachedScores(`score=${k}&limit=5000`)?.items);
  return lists.every(Boolean) ? { loading: false, summary, matches: lists.flat() } : null;
}

function useRecordsData() {
  const [state, setState] = useState(() => seededRecords() || { loading: true });
  useEffect(() => {
    let cancelled = false;
    const hadData = Boolean(state.summary);
    (async () => {
      const summary = (await fetchScores("summary=1")).items || [];
      const lists = await mapLimit(recordKeys(summary), 3, (k) => fetchScores(`score=${k}&limit=5000`).then((d) => d.items || []));
      if (!cancelled) setState({ loading: false, summary, matches: lists.flat() });
      // A failed refresh keeps the seeded tables on screen.
    })().catch(() => !cancelled && !hadData && setState({ loading: false, error: true }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
}

// `to` links the row's text to its scoreline page.
function Row({ date, text, badge, to }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr auto", sm: "150px 1fr auto" },
        alignItems: "center",
        gap: { xs: 0.25, sm: 2 },
        py: 1.1,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <Typography sx={{ color: BRAND.textSecondary, fontSize: 13, gridColumn: { xs: "1 / 2", sm: "auto" }, fontFeatureSettings: '"tnum"' }}>
        {date}
      </Typography>
      <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 700, gridColumn: { xs: "1 / 2", sm: "auto" }, gridRow: { xs: 2, sm: "auto" } }}>
        {to ? (
          <Box component={RouterLink} to={to} sx={{ color: "inherit", textDecoration: "none", "&:hover": { color: BRAND.cyan } }}>
            {text}
          </Box>
        ) : (
          text
        )}
      </Typography>
      {badge && (
        <Typography
          sx={{
            gridRow: { xs: "1 / 3", sm: "auto" },
            gridColumn: { xs: 2, sm: "auto" },
            justifySelf: "end",
            color: "#fff",
            fontSize: 11.5,
            fontWeight: 800,
            letterSpacing: "0.04em",
            px: 1.25,
            py: 0.4,
            borderRadius: 999,
            background: "rgba(111,93,245,0.25)",
            border: "1px solid rgba(111,93,245,0.45)",
            whiteSpace: "nowrap",
          }}
        >
          {badge}
        </Typography>
      )}
    </Box>
  );
}

function Table({ rows }) {
  return <Box sx={{ mb: 2 }}>{rows.map((r, i) => <Row key={i} {...r} />)}</Box>;
}

const matchText = (m) =>
  `${displayName(m.homeTeam)} ${m.homeTeamScore}–${m.awayTeamScore} ${displayName(m.awayTeam)}`;

const occurrenceText = (occ) => {
  const o = orientOccurrence(occ, HOME_AWAY);
  return `${o.leftTeam?.name} ${o.leftScore}–${o.rightScore} ${o.rightTeam?.name}`;
};

export default function RecordsPage() {
  const { loading, error, summary, matches } = useRecordsData();

  const data = useMemo(() => {
    if (!summary) return null;
    const cells = Object.values(summaryCells(summary, HOME_AWAY));
    const total = cells.reduce((acc, c) => acc + c.count, 0);
    const goals = (m) => m.homeTeamScore + m.awayTeamScore;
    const margin = (m) => Math.abs(m.homeTeamScore - m.awayTeamScore);
    const maxMargin = Math.max(0, ...matches.map(margin));
    const atMax = matches.filter((m) => margin(m) === maxMargin);
    const lastClassic = summary.reduce((best, r) => (!best || r.firstOccurrence.utcDate > best.utcDate ? r.firstOccurrence : best), null);
    return {
      total,
      scorelines: cells.length,
      classic: summary.length,
      lastClassic,
      maxMargin,
      atMax: atMax.length,
      atMaxAway: atMax.filter((m) => m.awayTeamScore > m.homeTeamScore).length,
      timeline: [...cells].sort((a, b) => b.firstOccurrence.utcDate.localeCompare(a.firstOccurrence.utcDate)),
      rare: cells.filter((c) => c.count <= 2).sort((a, b) => a.count - b.count || b.latestOccurrence.utcDate.localeCompare(a.latestOccurrence.utcDate)),
      biggest: matches.filter((m) => margin(m) >= 7).sort((a, b) => margin(b) - margin(a) || goals(b) - goals(a) || a.utcDate.localeCompare(b.utcDate)),
      highest: matches.filter((m) => goals(m) >= 9).sort((a, b) => goals(b) - goals(a) || a.utcDate.localeCompare(b.utcDate)),
      common: [...cells].sort((a, b) => b.count - a.count).slice(0, 10),
    };
  }, [summary, matches]);

  const loadingRows = (
    <Box sx={{ mb: 2 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} height={40} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />
      ))}
    </Box>
  );
  const section = (rows) => (loading ? loadingRows : error ? <P>The records couldn't be loaded right now. Please try again shortly.</P> : <Table rows={rows} />);

  return (
    <ProsePage>
      <SEO
        title="Premier League records: biggest wins and highest scores"
        description="The biggest wins, highest-scoring matches and rarest results in Premier League history, plus every scorigami in order since 1992. Updated after every match."
        path="/records"
      />
      <PageHeader
        eyebrow="Records"
        title="Premier League records: biggest wins, highest scores and rarest results"
        lede={
          data
            ? `${data.total.toLocaleString()} matches since August 1992 have produced ${data.scorelines} different home/away results (${data.classic} if you ignore venue). These are the milestones, one-offs and extremes behind the grid.`
            : "Every milestone, one-off and extreme result behind the Premier League scorigami grid since August 1992."
        }
      />

      <H2 id="timeline">The scorigami timeline</H2>
      <P>
        Every home/away result the Premier League has produced, in the order it
        first happened, most recent first. The first seasons filled most of the
        grid; since then new squares have arrived only every few years, and the
        classic count (ignoring venue) last moved
        {data?.lastClassic ? ` on ${formatDate(data.lastClassic.utcDate)}` : " in 2013"}.
      </P>
      {section((data?.timeline || []).map((c) => ({
        date: formatDate(c.firstOccurrence.utcDate),
        text: occurrenceText(c.firstOccurrence),
        badge: `first ${c.score.replace("-", "–")}`,
        to: `/scorelines/${toWinnerFirst(c.score)}`,
      })))}

      <H2 id="rarest">The rarest results</H2>
      <P>
        Home/away results that have happened no more than twice. Several are
        recent away demolitions, which is why the{" "}
        <InternalLink to="/articles/home-away-scorigami">home/away view</InternalLink>{" "}
        is where new scorigamis keep appearing.
      </P>
      {section((data?.rare || []).map((c) => ({
        date: formatDate(c.latestOccurrence.utcDate),
        text: occurrenceText(c.latestOccurrence),
        badge: c.count === 1 ? "only once" : "twice",
        to: `/scorelines/${toWinnerFirst(c.score)}`,
      })))}

      <AdSlot position="records" />

      <H2 id="biggest-wins">Biggest wins</H2>
      <P>
        Every Premier League match won by seven goals or more, biggest margin
        first.
        {data &&
          ` The record margin is ${data.maxMargin}, reached ${data.atMax} time${data.atMax === 1 ? "" : "s"}` +
            (data.atMaxAway ? `, ${data.atMaxAway === 1 ? "once" : `${data.atMaxAway} times`} by an away side.` : ".")}
      </P>
      {section((data?.biggest || []).map((m) => ({
        date: formatDate(m.utcDate),
        text: matchText(m),
        badge: `${Math.abs(m.homeTeamScore - m.awayTeamScore)}-goal win`,
        to: `/scorelines/${m.score}`,
      })))}

      <H2 id="highest-scoring">Highest-scoring matches</H2>
      <P>
        Every match with nine goals or more. Portsmouth 7–4 Reading in 2007 is
        still the only game with eleven; the stories behind the one-off
        scorelines are in{" "}
        <InternalLink to="/articles/rarest-premier-league-scorelines">the rarest scorelines in Premier League history</InternalLink>.
      </P>
      {section((data?.highest || []).map((m) => ({
        date: formatDate(m.utcDate),
        text: matchText(m),
        badge: `${m.homeTeamScore + m.awayTeamScore} goals`,
        to: `/scorelines/${m.score}`,
      })))}

      <H2 id="most-common">Most common results</H2>
      <P>
        The results that come round every weekend. A draw leads the home/away
        count because 1–1 has no mirror image, while every win is split between
        a home version and an away one.
      </P>
      {section((data?.common || []).map((c, i) => ({
        date: `#${i + 1}`,
        text: `${c.score.replace("-", "–")} (${parseKey(c.score)[0] === parseKey(c.score)[1] ? "draw" : parseKey(c.score)[0] > parseKey(c.score)[1] ? "home win" : "away win"})`,
        badge: `${c.count.toLocaleString()} · ${((c.count / data.total) * 100).toFixed(1)}%`,
        to: `/scorelines/${toWinnerFirst(c.score)}`,
      })))}

      <CtaRow
        links={[
          { to: "/", label: "Explore the grid", primary: true },
          { to: "/scorelines", label: "All scorelines" },
          { to: "/articles", label: "Articles" },
          { to: "/faq", label: "FAQ" },
        ]}
      />
    </ProsePage>
  );
}
